import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { keyedPath } from '@/shared/api/keyedPath';
import { helpStaticPath, type HelpIndexEntry, type HelpScreenDoc } from './model';
import { resolveHelpRoute } from './resolveHelpRoute';
import type { HelpLayer, HelpLayerGap, HelpLayerReview } from './layerTypes';

/**
 * Data hooks for the living help panel (ADR-0066).
 *
 * <p>The master help is a STATIC asset compiled by `scripts/help-build.mjs` into
 * `frontend/public/help-static/<route>.json`, so it is read with plain `fetch`, not `api()`
 * (no bearer token, no ProblemDetail, no 401 handling). The per-client layer (gap status,
 * answers, review) is a real API call under `/api/help`; route and gap id ride the query string
 * on a `_` path segment because an encoded `%2F` in a path segment is rejected before Spring
 * (John's slice-3993 contract, docs/help_project/requests.md).
 */

/** Long: the static file only changes with a deploy. */
const STATIC_STALE_MS = 30 * 60_000;

export const helpDocKey = (route: string) => ['help-static', route] as const;
export const helpLayerKey = (route: string) => ['help', route, 'layer'] as const;

/**
 * Fetch one screen's compiled help. `null` = "no help yet": a 404, or an HTML body (the dev
 * server and nginx both answer an unknown path with the SPA's index.html and a 200).
 */
export async function fetchHelpScreen(route: string): Promise<HelpScreenDoc | null> {
  const res = await fetch(`/help-static/${helpStaticPath(route)}`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Help for ${route} could not be loaded (HTTP ${res.status}).`);
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) return null;
  const body = (await res.json()) as HelpScreenDoc | null;
  // Anything that is not a compiled doc (e.g. a JSON error body) reads as "no help yet".
  return body && Array.isArray(body.sections) ? body : null;
}

/**
 * `/help-static/index.json` (one row per screen that has help). `null` when the build has not
 * emitted it yet (404 / SPA fallback HTML).
 */
export async function fetchHelpIndex(): Promise<HelpIndexEntry[] | null> {
  const res = await fetch('/help-static/index.json', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Help index could not be loaded (HTTP ${res.status}).`);
  if (!(res.headers.get('content-type') ?? '').includes('json')) return null;
  const body: unknown = await res.json();
  return Array.isArray(body) ? (body as HelpIndexEntry[]) : null;
}

export function useHelpIndex() {
  return useQuery<HelpIndexEntry[] | null>({
    queryKey: ['help-static', '_index'],
    queryFn: fetchHelpIndex,
    staleTime: STATIC_STALE_MS,
    retry: false,
  });
}

/**
 * The catalog route whose help the live `pathname` shows (see {@link resolveHelpRoute}).
 * `undefined` while the index loads; `null` when no indexed screen covers the pathname. If the
 * index itself is missing or failed, the pathname is used as-is (a 404 then reads as "no help").
 */
export function useResolvedHelpRoute(pathname: string): string | null | undefined {
  const indexQ = useHelpIndex();
  if (indexQ.isPending) return undefined;
  if (!indexQ.data) return pathname;
  return resolveHelpRoute(pathname, indexQ.data.map((e) => e.route));
}

/** Compiled help for a RESOLVED catalog route; pass null for "no help" (no fetch). */
export function useHelpScreen(route: string | null) {
  return useQuery<HelpScreenDoc | null>({
    queryKey: helpDocKey(route ?? ''),
    queryFn: () => (route ? fetchHelpScreen(route) : Promise.resolve(null)),
    enabled: route != null,
    staleTime: STATIC_STALE_MS,
    retry: false,
  });
}

// Route and gap id are PowerHouse-style free text (`/` in every route); keyedPath writes the
// backend's `_` sibling URL with the key in the query string (card 118, John's contract in
// docs/help_project/requests.md).

export function useHelpLayer(route: string, enabled: boolean) {
  return useQuery<HelpLayer>({
    queryKey: helpLayerKey(route),
    queryFn: () => api<HelpLayer>(keyedPath('/api/help/screens/{route}/layer', { route })),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function answerGap(id: string, answerMd: string): Promise<HelpLayerGap> {
  return api<HelpLayerGap>(keyedPath('/api/help/gaps/{id}/answer', { id }), {
    method: 'POST',
    json: { answerMd },
  });
}

export function resolveGap(id: string): Promise<HelpLayerGap> {
  return api<HelpLayerGap>(keyedPath('/api/help/gaps/{id}/resolve', { id }), { method: 'POST' });
}

export function rejectGap(id: string, note?: string): Promise<HelpLayerGap> {
  const trimmed = note?.trim();
  return api<HelpLayerGap>(keyedPath('/api/help/gaps/{id}/reject', { id }), {
    method: 'POST',
    json: trimmed ? { note: trimmed } : {},
  });
}

export function markReviewed(route: string, version?: string): Promise<HelpLayerReview> {
  return api<HelpLayerReview>(keyedPath('/api/help/screens/{route}/reviewed', { route }), {
    method: 'POST',
    json: version ? { version } : {},
  });
}

/** Every gap/review write re-reads the layer for this route (the server is the truth). */
function useInvalidateLayer(route: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['help', route] });
}

export function useAnswerGap(route: string) {
  const invalidate = useInvalidateLayer(route);
  return useMutation<HelpLayerGap, Error, { id: string; answerMd: string }>({
    mutationFn: ({ id, answerMd }) => answerGap(id, answerMd),
    onSuccess: invalidate,
  });
}

export function useResolveGap(route: string) {
  const invalidate = useInvalidateLayer(route);
  return useMutation<HelpLayerGap, Error, { id: string }>({
    mutationFn: ({ id }) => resolveGap(id),
    onSuccess: invalidate,
  });
}

export function useRejectGap(route: string) {
  const invalidate = useInvalidateLayer(route);
  return useMutation<HelpLayerGap, Error, { id: string; note?: string }>({
    mutationFn: ({ id, note }) => rejectGap(id, note),
    onSuccess: invalidate,
  });
}

export function useMarkReviewed(route: string) {
  const invalidate = useInvalidateLayer(route);
  return useMutation<HelpLayerReview, Error, { version?: string }>({
    mutationFn: ({ version }) => markReviewed(route, version),
    onSuccess: invalidate,
  });
}
